// 🔧 points_results_helpers.js
// ✅ Purpose: Results engine for Judged Points Voting
// ✅ Features:
// - Aggregates Judged Points votes by entry
// - Calculates average total score per entry
// - Calculates judge/vote count per entry
// - Builds Top Overall leaderboard results
// - Aggregates scores by scoring factor
// - Aggregates Detailed Scorecard criteria/highlights by entry
// - Calculates criterion judge count, total score, and average score
// - Builds Top results per scoring factor
// - Includes Detailed Scorecard criterion aggregates in the unified results package
// - Exposes all ranked evaluated entries for detailed judged dashboards
// - Calculates normalized overall scorecard averages when criteria share a common point scale
// - Hydrates result rows with entry/car display data
// - Keeps Judged Points math out of leaderboard.js, ballot.js, audit.js, and print pages
// - Fully block-commented for clarity and future devs

/**
 * ✅ Safely converts values into numbers.
 */
export function toSafeNumber(value) {
  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return 0;
  }

  return numberValue;
}

/**
 * ✅ Builds a reusable display name for an entry owner.
 */
export function buildOwnerDisplayName(entry = {}) {
  return (
    `${entry.ownerFirst || ""} ${entry.ownerLast || ""}`.trim() ||
    entry.ownerName ||
    entry.owner ||
    "Unknown Owner"
  );
}

/**
 * ✅ Filters only Judged Points vote documents.
 */
export function getJudgedPointsVotes(votes = []) {
  return votes.filter((vote) => vote?.votingType === "points");
}

/**
 * ✅ Aggregates Judged Points votes by entry.
 *
 * Final score rule:
 * - Add all totalPoints for an entry
 * - Divide by number of judges/voters who scored that entry
 */
export function aggregateJudgedPointsByEntry(votes = []) {
  const pointsVotes = getJudgedPointsVotes(votes);
  const entryMap = {};

  pointsVotes.forEach((vote) => {
    if (!vote?.cid) {
      return;
    }

    if (!entryMap[vote.cid]) {
      entryMap[vote.cid] = {
        cid: vote.cid,
        judgeCount: 0,
        totalScoreSum: 0,
        averageScore: 0,
        maxBallotPoints: toSafeNumber(vote.maxBallotPoints || 100),
        votes: [],
      };
    }

    entryMap[vote.cid].judgeCount += 1;
    entryMap[vote.cid].totalScoreSum += toSafeNumber(vote.totalPoints);
    entryMap[vote.cid].votes.push(vote);
  });

  return Object.values(entryMap).map((entryResult) => {
    const averageScore =
      entryResult.judgeCount > 0
        ? entryResult.totalScoreSum / entryResult.judgeCount
        : 0;

    return {
      ...entryResult,
      averageScore,
      averageScoreDisplay: averageScore.toFixed(2),
    };
  });
}

/**
 * ✅ Hydrates entry results with car/entry data.
 *
 * carMap expected shape:
 * {
 *   [cid]: {
 *     regcnt,
 *     description,
 *     ownerFirst,
 *     ownerLast
 *   }
 * }
 */
export function hydrateJudgedPointsRows(rows = [], carMap = {}) {
  return rows.map((row) => {
    const entry = carMap[row.cid] || carMap[String(row.cid)] || {};

    return {
      ...row,
      entry,
      regcnt: entry.regcnt || "",
      description: entry.description || "",
      name: buildOwnerDisplayName(entry),
    };
  });
}

/**
 * ✅ Builds the overall judged points leaderboard.
 */
export function buildJudgedPointsOverallLeaderboard({
  votes = [],
  carMap = {},
  limit = 3,
} = {}) {
  const aggregatedRows = aggregateJudgedPointsByEntry(votes);

  const sortedRows = aggregatedRows
    .sort((a, b) => {
      if (b.averageScore !== a.averageScore) {
        return b.averageScore - a.averageScore;
      }

      // ✅ Tie-breaker 1: more judges scored the entry
      if (b.judgeCount !== a.judgeCount) {
        return b.judgeCount - a.judgeCount;
      }

      // ✅ Tie-breaker 2: higher raw total score sum
      return b.totalScoreSum - a.totalScoreSum;
    })
    .slice(0, limit)
    .map((row, index) => ({
      ...row,
      placement: index + 1,
    }));

  return hydrateJudgedPointsRows(sortedRows, carMap);
}

/**
 * ✅ Aggregates Judged Points votes by scoring factor.
 *
 * Example output:
 * [
 *   {
 *     factorId: "body",
 *     factorName: "Body",
 *     maxPoints: 15,
 *     entries: [
 *       {
 *         cid: "abc",
 *         judgeCount: 3,
 *         totalScoreSum: 42,
 *         averageScore: 14
 *       }
 *     ]
 *   }
 * ]
 */
export function aggregateJudgedPointsByFactor(votes = []) {
  const pointsVotes = getJudgedPointsVotes(votes);
  const factorMap = {};

  pointsVotes.forEach((vote) => {
    if (!vote?.cid || !Array.isArray(vote?.scores)) {
      return;
    }

    vote.scores.forEach((score) => {
      if (!score?.factorId) {
        return;
      }

      if (!factorMap[score.factorId]) {
        factorMap[score.factorId] = {
          factorId: score.factorId,
          factorName: score.factorName || "Unnamed Factor",
          maxPoints: toSafeNumber(score.maxPoints),
          entries: {},
        };
      }

      if (!factorMap[score.factorId].entries[vote.cid]) {
        factorMap[score.factorId].entries[vote.cid] = {
          cid: vote.cid,
          factorId: score.factorId,
          factorName: score.factorName || "Unnamed Factor",
          maxPoints: toSafeNumber(score.maxPoints),
          judgeCount: 0,
          totalScoreSum: 0,
          averageScore: 0,
        };
      }

      factorMap[score.factorId].entries[vote.cid].judgeCount += 1;
      factorMap[score.factorId].entries[vote.cid].totalScoreSum +=
        toSafeNumber(score.pointsAwarded);
    });
  });

  return Object.values(factorMap).map((factor) => {
    const entries = Object.values(factor.entries).map((entryResult) => {
      const averageScore =
        entryResult.judgeCount > 0
          ? entryResult.totalScoreSum / entryResult.judgeCount
          : 0;

      return {
        ...entryResult,
        averageScore,
        averageScoreDisplay: averageScore.toFixed(2),
      };
    });

    return {
      ...factor,
      entries,
    };
  });
}

/**
 * ✅ Aggregates Detailed Scorecard criteria by scoring factor and entry.
 *
 * Detailed Scorecard criteria are persisted inside each Points vote as:
 *
 * scores[] -> checkedHighlights[]
 *
 * Only highlights with maxPoints greater than zero are treated as scored
 * criteria. This keeps ordinary Points-mode key highlights out of detailed
 * criterion result calculations.
 *
 * Example output:
 * [
 *   {
 *     factorId: "innovation",
 *     factorName: "Innovation",
 *     criteria: [
 *       {
 *         criterionId: "originality",
 *         criterionName: "Originality",
 *         maxPoints: 5,
 *         entries: [
 *           {
 *             cid: "partner-a",
 *             judgeCount: 2,
 *             totalScoreSum: 9,
 *             averageScore: 4.5,
 *             averageScoreDisplay: "4.50"
 *           }
 *         ]
 *       }
 *     ]
 *   }
 * ]
 */
export function aggregateJudgedPointsByCriterion(votes = []) {
  const pointsVotes = getJudgedPointsVotes(votes);
  const factorMap = {};

  pointsVotes.forEach((vote) => {
    if (!vote?.cid || !Array.isArray(vote?.scores)) {
      return;
    }

    vote.scores.forEach((score) => {
      if (!score?.factorId || !Array.isArray(score?.checkedHighlights)) {
        return;
      }

      score.checkedHighlights.forEach((highlight) => {
        const criterionId = highlight?.highlightId;
        const maxPoints = toSafeNumber(highlight?.maxPoints);

        // ✅ Detailed Scorecard criteria are identified by a positive max score.
        // Ordinary Points-mode highlights do not participate here.
        if (!criterionId || maxPoints <= 0) {
          return;
        }

        if (!factorMap[score.factorId]) {
          factorMap[score.factorId] = {
            factorId: score.factorId,
            factorName: score.factorName || "Unnamed Factor",
            criteria: {},
          };
        }

        const factorResult = factorMap[score.factorId];

        if (!factorResult.criteria[criterionId]) {
          factorResult.criteria[criterionId] = {
            criterionId,
            criterionName:
              highlight.highlightName ||
              "Unnamed Criterion",
            maxPoints,
            entries: {},
          };
        }

        const criterionResult =
          factorResult.criteria[criterionId];

        if (!criterionResult.entries[vote.cid]) {
          criterionResult.entries[vote.cid] = {
            cid: vote.cid,
            factorId: score.factorId,
            factorName:
              score.factorName ||
              "Unnamed Factor",
            criterionId,
            criterionName:
              highlight.highlightName ||
              "Unnamed Criterion",
            maxPoints,
            judgeCount: 0,
            totalScoreSum: 0,
            averageScore: 0,
            averageScoreDisplay: "0.00",
          };
        }

        const entryResult =
          criterionResult.entries[vote.cid];

        // ✅ A criterion that exists in this saved vote counts as one
        // judge evaluation, including a legitimate explicit score of zero.
        entryResult.judgeCount += 1;
        entryResult.totalScoreSum +=
          toSafeNumber(highlight.pointsAwarded);
      });
    });
  });

  return Object.values(factorMap).map((factor) => ({
    factorId: factor.factorId,
    factorName: factor.factorName,

    criteria: Object.values(factor.criteria).map((criterion) => ({
      criterionId: criterion.criterionId,
      criterionName: criterion.criterionName,
      maxPoints: criterion.maxPoints,

      entries: Object.values(criterion.entries).map((entryResult) => {
        const averageScore =
          entryResult.judgeCount > 0
            ? entryResult.totalScoreSum /
              entryResult.judgeCount
            : 0;

        return {
          ...entryResult,
          averageScore,
          averageScoreDisplay:
            averageScore.toFixed(2),
        };
      }),
    })),
  }));
}

/**
 * ✅ Builds Top results per scoring factor.
 */
export function buildJudgedPointsFactorLeaderboards({
  votes = [],
  carMap = {},
  limit = 3,
} = {}) {
  return aggregateJudgedPointsByFactor(votes).map((factor) => {
    const topEntries = factor.entries
      .sort((a, b) => {
        if (b.averageScore !== a.averageScore) {
          return b.averageScore - a.averageScore;
        }

        if (b.judgeCount !== a.judgeCount) {
          return b.judgeCount - a.judgeCount;
        }

        return b.totalScoreSum - a.totalScoreSum;
      })
      .slice(0, limit)
      .map((row, index) => ({
        ...row,
        placement: index + 1,
      }));

    return {
      factorId: factor.factorId,
      factorName: factor.factorName,
      maxPoints: factor.maxPoints,
      topEntries: hydrateJudgedPointsRows(topEntries, carMap),
    };
  });
}

/**
 * ✅ Builds the complete Judged Points results package.
 *
 * Used by leaderboard.js and leaderboard_print.js.
 */
/**
 * ✅ Adds a normalized overall score to hydrated Judged Points entries.
 *
 * Detailed Scorecard criteria can expose a familiar common scale such as
 * 1–5 even when the authoritative ballot total is 25, 50, 100, etc.
 *
 * Business rules:
 * - Uses already-aggregated criterion results.
 * - Each actual criterion observation receives equal weight.
 * - Explicit zero scores remain valid observations.
 * - Missing criterion observations are not invented as zeros.
 * - Normalization is exposed only when all observed criteria for the
 *   entry use the same positive maximum score.
 * - Existing ranking fields and raw ballot averages remain unchanged.
 */
export function applyJudgedPointsNormalizedOverall({
  entries = [],
  criterionResults = [],
} = {}) {
  return entries.map((entry) => {
    const observations = [];

    for (const factor of criterionResults || []) {
      for (const criterion of factor.criteria || []) {
        const criterionEntry =
          (criterion.entries || []).find(
            (resultEntry) =>
              String(resultEntry.cid) === String(entry.cid)
          );

        if (!criterionEntry) {
          continue;
        }

        const maxPoints =
          Number(criterion.maxPoints || criterionEntry.maxPoints || 0);

        const judgeCount =
          Number(criterionEntry.judgeCount || 0);

        const totalScoreSum =
          Number(criterionEntry.totalScoreSum || 0);

        if (
          !Number.isFinite(maxPoints) ||
          maxPoints <= 0 ||
          !Number.isFinite(judgeCount) ||
          judgeCount <= 0 ||
          !Number.isFinite(totalScoreSum)
        ) {
          continue;
        }

        observations.push({
          maxPoints,
          judgeCount,
          totalScoreSum,
        });
      }
    }

    if (observations.length === 0) {
      return {
        ...entry,
        normalizedAverageScore: null,
        normalizedAverageScoreDisplay: null,
        normalizedMaxPoints: null,
      };
    }

    const scaleValues =
      Array.from(
        new Set(
          observations.map(
            (observation) => observation.maxPoints
          )
        )
      );

    // ✅ A single familiar display scale is only valid when every
    // observed criterion uses the same maximum.
    if (scaleValues.length !== 1) {
      return {
        ...entry,
        normalizedAverageScore: null,
        normalizedAverageScoreDisplay: null,
        normalizedMaxPoints: null,
      };
    }

    const totalScoreSum =
      observations.reduce(
        (sum, observation) =>
          sum + observation.totalScoreSum,
        0
      );

    const observationCount =
      observations.reduce(
        (sum, observation) =>
          sum + observation.judgeCount,
        0
      );

    if (observationCount <= 0) {
      return {
        ...entry,
        normalizedAverageScore: null,
        normalizedAverageScoreDisplay: null,
        normalizedMaxPoints: null,
      };
    }

    const normalizedAverageScore =
      totalScoreSum / observationCount;

    return {
      ...entry,
      normalizedAverageScore,
      normalizedAverageScoreDisplay:
        normalizedAverageScore.toFixed(2),
      normalizedMaxPoints:
        scaleValues[0],
    };
  });
}

export function buildJudgedPointsResults({
  votes = [],
  carMap = {},
  limit = 3,
} = {}) {
  const overallTopEntries = buildJudgedPointsOverallLeaderboard({
    votes,
    carMap,
    limit,
  });

  const factorLeaderboards = buildJudgedPointsFactorLeaderboards({
    votes,
    carMap,
    limit,
  });

  // ✅ Detailed Scorecard criterion aggregates.
  // These are built from persisted checkedHighlights[] rows inside
  // the existing Points vote documents.
  const criterionResults = aggregateJudgedPointsByCriterion(votes);

  // ✅ Complete evaluated-entry collection for detailed judged results.
  // Unlike overallTopEntries, this collection is intentionally not
  // restricted by the configured public leaderboard limit.
  // Reusing the Overall builder preserves identical ranking, tie-break,
  // placement, and entry-hydration behavior.
  const allEntries = buildJudgedPointsOverallLeaderboard({
    votes,
    carMap,
    limit: Number.MAX_SAFE_INTEGER,
  });

  const totalScorecards = getJudgedPointsVotes(votes).length;

  const normalizedOverallTopEntries =
    applyJudgedPointsNormalizedOverall({
      entries: overallTopEntries,
      criterionResults,
    });

  const normalizedAllEntries =
    applyJudgedPointsNormalizedOverall({
      entries: allEntries,
      criterionResults,
    });

  return {
    totalScorecards,
    overallTopEntries: normalizedOverallTopEntries,
    allEntries: normalizedAllEntries,
    factorLeaderboards,
    criterionResults,
  };
}